import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Skeleton } from 'src/common';
import CompanyTypes from '../CompanyTypes';
import CpvCodesTree from '../CpvCodesTree';
import styles from './index.module.scss';
import NutsCodesTree from '../NutCodesTree';
import AccessControlList from '../AccessControlList';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';
import { useLoadMp } from 'src/models/matchingProfiles/hooks';
import MatchingProfileName from '../MatchingProfileName';
import CompaniesFilter from '../CompaniesFilter';
import Keywords from '../Keywords';
import MpFooter from '../Footer';
import Accordion from 'src/common/Accordion';
import { BuyerTypesFilter, ContractValueMpFilter, Title } from 'src/shared';
import { faBuilding, faCoins, faMapMarkedAlt, faRadar, faTelescope, faUsers } from '@fortawesome/pro-light-svg-icons';
import CopyMatchingProfile from './CopyMatchingProfile';

export const MatchingProfile: FC = () => {
  const { t } = useTranslation();
  const { data: mp, loading: mpLoading } = useLoadMp();

  const accessToEdit = useMemo(() => {
    return mp?.accessToEdit;
  }, [mp]);
  const isMpAclFeature = useFeatureFlag(FeatureFlag.MatchingProfile_ACL);
  const isMpAdvanced = useFeatureFlag(FeatureFlag.MatchingProfile_AdvancedFilters);
  const isBTagsAvailable = useMemo(() => !!mp?.bTagIds && !!mp.bTagIds.length, [mp]);

  return mpLoading ? (
    <Skeleton loading active />
  ) : (
    <div className={styles.container}>
      <MatchingProfileName />
      {!accessToEdit && !mpLoading && <Alert message={t('Tenders.infoAboutMPSubscribers')} type={'info'} />}
      {isMpAclFeature && (
        <Accordion
          label={
            <Title icon={{ defaultIcon: faUsers }} className={styles.title}>
              {t('Tenders.permission')}
            </Title>
          }
          className={styles.accordion}
        >
          <AccessControlList readOnly={!accessToEdit} />
        </Accordion>
      )}
      {isBTagsAvailable && <CompanyTypes disabled={!accessToEdit} />}
      {isMpAdvanced && (
        <Accordion
          label={
            <Title icon={{ defaultIcon: faCoins }} className={styles.title}>
              {t('ExpiringContracts.estimatedContractValue')}
            </Title>
          }
          className={styles.accordion}
        >
          <ContractValueMpFilter disabled={!accessToEdit} />
        </Accordion>
      )}
      <Accordion
        label={
          <Title icon={{ defaultIcon: faRadar }} className={styles.title}>
            {t('Tenders.keywords')}
          </Title>
        }
        className={styles.accordion}
      >
        <Keywords disabled={!accessToEdit} />
      </Accordion>
      <Accordion
        label={
          <Title icon={{ defaultIcon: faBuilding }} className={styles.title}>
            {t('Tenders.BuyersFilter.buyers')}
          </Title>
        }
        className={styles.accordion}
      >
        <CompaniesFilter disabled={!accessToEdit} />
        {isMpAdvanced && <BuyerTypesFilter disabled={!accessToEdit} />}
      </Accordion>
      <Accordion
        label={
          <Title icon={{ defaultIcon: faMapMarkedAlt }} className={styles.title}>
            {t('Tenders.nutsCodes')}
          </Title>
        }
        className={styles.accordion}
      >
        <NutsCodesTree disabled={!accessToEdit} />
      </Accordion>
      <Accordion
        label={
          <Title icon={{ defaultIcon: faTelescope }} className={styles.title}>
            {t('Tenders.cpvCodes')}
          </Title>
        }
        className={styles.accordion}
      >
        <CpvCodesTree disabled={!accessToEdit} />
      </Accordion>
      <CopyMatchingProfile />
      <MpFooter />
    </div>
  );
};

export default MatchingProfile;
